/**
 * Grid A* for guard investigation + alert movement. 8-directional with
 * √2 diagonal cost; refuses to cut diagonally through a wall corner
 * (both orthogonal neighbors must also be walkable).
 *
 * Caller supplies `walkable(x, y)` so the same routine handles map-only
 * queries and entity-avoidance queries. Optional `costBias` adds extra
 * cost per tile — used by phase-4 alert combat to steer guards toward
 * cover routes.
 *
 * Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

export interface Point {
  x: number;
  y: number;
}

export interface PathfindOptions {
  /** Optional per-tile cost surcharge (e.g. penalize open tiles under fire). */
  costBias?: (x: number, y: number) => number;
  /** Cap on nodes popped from the open set. Default 4000. Returns null if hit. */
  maxNodes?: number;
  /** If true, goal only needs to be adjacent (8-neighbor) not reached exactly. */
  adjacentGoal?: boolean;
  /** If true, allow diagonal moves that pass through a wall corner. Default false. */
  allowCornerCut?: boolean;
}

const SQRT2 = Math.SQRT2;

/** Straight-line 8-dir neighbor deltas: E, W, S, N, SE, SW, NE, NW. */
const NEIGHBORS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, SQRT2],
  [-1, 1, SQRT2],
  [1, -1, SQRT2],
  [-1, -1, SQRT2],
];

/**
 * Find a shortest 8-directional path from `start` to `goal`.
 * Returns the path as an array of tile coords **including** start and goal,
 * or `null` if unreachable / node cap exceeded / goal itself unwalkable
 * (unless `adjacentGoal`).
 */
export function findPath(
  start: Point,
  goal: Point,
  walkable: (x: number, y: number) => boolean,
  options: PathfindOptions = {},
): Point[] | null {
  const { costBias, maxNodes = 4000, adjacentGoal = false, allowCornerCut = false } = options;

  if (start.x === goal.x && start.y === goal.y) return [{ x: start.x, y: start.y }];
  if (!adjacentGoal && !walkable(goal.x, goal.y)) return null;

  const key = (x: number, y: number): string => `${x},${y}`;
  const parent = new Map<string, string | null>();
  const gScore = new Map<string, number>();
  const open = new BinaryHeap<HeapNode>((a, b) => a.f - b.f);

  const startKey = key(start.x, start.y);
  parent.set(startKey, null);
  gScore.set(startKey, 0);
  open.push({ x: start.x, y: start.y, f: heuristic(start.x, start.y, goal.x, goal.y) });

  let popped = 0;
  while (open.size() > 0) {
    const cur = open.pop()!;
    popped++;
    if (popped > maxNodes) return null;
    const curKey = key(cur.x, cur.y);
    const curG = gScore.get(curKey) ?? Infinity;

    // Reached (either exactly or adjacent, per options).
    if (isGoal(cur.x, cur.y, goal, adjacentGoal)) {
      return reconstruct(parent, curKey);
    }

    for (const [dx, dy, base] of NEIGHBORS) {
      const nx = cur.x + dx;
      const ny = cur.y + dy;
      // Goal squeeze exception: if the neighbor IS the goal and adjacentGoal
      // is on, we let it through even if unwalkable (walking up to it).
      const isGoalTile = nx === goal.x && ny === goal.y;
      if (!walkable(nx, ny) && !(adjacentGoal && isGoalTile)) continue;

      // Diagonal corner-cut guard: both orthogonal neighbors must be walkable
      // so guards can't slip diagonally through a wall corner.
      if (!allowCornerCut && dx !== 0 && dy !== 0) {
        if (!walkable(cur.x + dx, cur.y) || !walkable(cur.x, cur.y + dy)) continue;
      }

      const step = base + (costBias?.(nx, ny) ?? 0);
      const tentativeG = curG + step;
      const nKey = key(nx, ny);
      if (tentativeG < (gScore.get(nKey) ?? Infinity)) {
        parent.set(nKey, curKey);
        gScore.set(nKey, tentativeG);
        open.push({ x: nx, y: ny, f: tentativeG + heuristic(nx, ny, goal.x, goal.y) });
      }
    }
  }

  return null;
}

/** Octile distance — admissible for 8-dir movement with unit / √2 costs. */
function heuristic(x0: number, y0: number, x1: number, y1: number): number {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const min = Math.min(dx, dy);
  const max = Math.max(dx, dy);
  return (SQRT2 - 1) * min + max;
}

function isGoal(x: number, y: number, goal: Point, adjacent: boolean): boolean {
  if (x === goal.x && y === goal.y) return true;
  if (!adjacent) return false;
  return Math.max(Math.abs(x - goal.x), Math.abs(y - goal.y)) <= 1;
}

function reconstruct(parent: Map<string, string | null>, endKey: string): Point[] {
  const out: Point[] = [];
  let k: string | null = endKey;
  while (k !== null) {
    const [xs, ys] = k.split(',');
    out.push({ x: Number(xs), y: Number(ys) });
    k = parent.get(k) ?? null;
  }
  out.reverse();
  return out;
}

interface HeapNode {
  x: number;
  y: number;
  f: number;
}

/** Minimal binary min-heap. Small enough that a full library would be silly. */
class BinaryHeap<T> {
  private readonly data: T[] = [];
  constructor(private readonly cmp: (a: T, b: T) => number) {}

  size(): number {
    return this.data.length;
  }

  push(v: T): void {
    this.data.push(v);
    this.siftUp(this.data.length - 1);
  }

  pop(): T | undefined {
    const n = this.data.length;
    if (n === 0) return undefined;
    const top = this.data[0]!;
    const last = this.data.pop()!;
    if (n > 1) {
      this.data[0] = last;
      this.siftDown(0);
    }
    return top;
  }

  private siftUp(i: number): void {
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.cmp(this.data[i]!, this.data[p]!) >= 0) break;
      [this.data[i], this.data[p]] = [this.data[p]!, this.data[i]!];
      i = p;
    }
  }

  private siftDown(i: number): void {
    const n = this.data.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let best = i;
      if (l < n && this.cmp(this.data[l]!, this.data[best]!) < 0) best = l;
      if (r < n && this.cmp(this.data[r]!, this.data[best]!) < 0) best = r;
      if (best === i) break;
      [this.data[i], this.data[best]] = [this.data[best]!, this.data[i]!];
      i = best;
    }
  }
}
